import { AxiosResponse } from 'axios';

import type { GetSubscriptionsActiveResponse } from '@calm-web/api-sdk/lib/app-api';

import { ApiRequest } from '@/hooks/api';

import { isSubscriptionResponse } from './types';

// The async payment flow returns immediately after the PaymentIntent is
// created, but `subscriptions/active` is populated only after Stripe's webhook
// fires. Poll for ~30s to ride out the webhook delay before surfacing an
// error to the user.
export const ACTIVE_SUBSCRIPTION_POLL_INTERVAL_MS = 1000;
export const ACTIVE_SUBSCRIPTION_POLL_MAX_ATTEMPTS = 30;

// Resolves after `ms`, or rejects early with an `AbortError` if `signal` aborts — so an unmounted
// caller stops waiting immediately instead of running out its poll budget in the background.
const wait = (ms: number, signal?: AbortSignal): Promise<void> =>
	new Promise((resolve, reject) => {
		if (signal?.aborted) {
			reject(signal.reason ?? new DOMException('Aborted', 'AbortError'));
			return;
		}
		// A `const` holder lets `onAbort` (declared before the timer) clear the timer without a
		// use-before-define / prefer-const bind cycle.
		const handles: { timer?: ReturnType<typeof setTimeout> } = {};
		const onAbort = () => {
			clearTimeout(handles.timer);
			reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
		};
		handles.timer = setTimeout(() => {
			// Remove the abort listener on the normal resolve path so listeners don't accumulate on
			// the signal across a multi-attempt poll.
			signal?.removeEventListener('abort', onAbort);
			resolve();
		}, ms);
		signal?.addEventListener('abort', onAbort, { once: true });
	});

export const fetchActiveSubscriptionWithPolling = async (
	apiRequest: ApiRequest,
	{
		pollIntervalMs = ACTIVE_SUBSCRIPTION_POLL_INTERVAL_MS,
		maxAttempts = ACTIVE_SUBSCRIPTION_POLL_MAX_ATTEMPTS,
		// When true, a 200 carrying a subscription-shaped but NON-valid (`valid: false`) body is
		// treated as "not fulfilled yet" and polled past, exactly like a 404. `subscriptions/active`
		// selects the most-relevant row by `expires` (has-non-null-expires), NOT by validity,
		// so during a resubscribe-after-cancel window it 200s with the stale/lapsed CANCELED
		// sub (`valid:false`) while the new sub is still `incomplete` (no row). Without this,
		// the caller would accept that canceled sub as a confirmed purchase. A genuinely
		// non-subscription / malformed 200 is NOT polled past — it won't become valid, so we
		// return it immediately (the caller surfaces it) rather than burning the whole budget.
		// Off by default to preserve existing callers' behavior.
		requireValid = false,
		// Aborts the poll (from a caller's effect/unmount cleanup): stops issuing further requests
		// and rejects an in-flight `wait` immediately.
		signal,
	}: { pollIntervalMs?: number; maxAttempts?: number; requireValid?: boolean; signal?: AbortSignal } = {},
): Promise<AxiosResponse<GetSubscriptionsActiveResponse>> => {
	for (let attempt = 1; ; attempt += 1) {
		if (signal?.aborted) {
			throw signal.reason ?? new DOMException('Aborted', 'AbortError');
		}
		try {
			const response = await apiRequest<GetSubscriptionsActiveResponse>({
				service: 'api',
				endpoint: 'subscriptions/active',
				method: 'GET',
				// Bust intermediate caches: the endpoint transitions from 404 to 200
				// during the polling window once Stripe's webhook lands, and a cached
				// 404 would otherwise stick for the full retry budget.
				noCache: true,
			});

			// Keep polling ONLY for a subscription-shaped body that is still `valid:false` (the
			// resubscribe webhook-lag window). A valid sub — or a genuinely non-subscription /
			// malformed body, which will never flip to valid — returns immediately.
			if (requireValid && isSubscriptionResponse(response.data) && !response.data.valid) {
				if (attempt >= maxAttempts) {
					return response;
				}
				await wait(pollIntervalMs, signal);
				continue;
			}

			return response;
		} catch (err) {
			// Aborted (unmount) → stop now; don't treat it as a retryable 404.
			if (signal?.aborted) {
				throw err;
			}
			// `apiRequest`'s axios interceptor flattens `error.response` onto the rejected
			// value, so the HTTP status lives on the top level of `err`.
			const status = (err as { status?: number } | undefined)?.status;
			if (status !== 404 || attempt >= maxAttempts) {
				throw err;
			}
			await wait(pollIntervalMs, signal);
		}
	}
};
